// v2.0.0 - 2026-01-31 - Calculations list page with inline form

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/authStore'
import { ti, cer } from '@/lib/api/client'
import type { TICalculation } from '@/types'
import { PageContainer, PageHeader } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'

type CalculationStatus = 'draft' | 'approved' | 'distributed'

interface Configuration {
  id: string
  name: string
  crin: string
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)
}

const formatPeriod = (period: string): string => {
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
  const [year, month] = period.split('-')
  return `${months[parseInt(month) - 1]} ${year}`
}

export default function CalculationsPage() {
  const { user } = useAuthStore()
  const [calculations, setCalculations] = useState<TICalculation[]>([])
  const [configurations, setConfigurations] = useState<Configuration[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [periodFilter, setPeriodFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<CalculationStatus | ''>('')
  const [configFilter, setConfigFilter] = useState('')

  // Pagination
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const limit = 10

  // New calculation modal
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newCalcConfig, setNewCalcConfig] = useState('')
  const [newCalcPeriod, setNewCalcPeriod] = useState('')
  const [newCalcTiTotal, setNewCalcTiTotal] = useState('')
  const [newCalcEaciTotal, setNewCalcEaciTotal] = useState('')
  const [preview, setPreview] = useState<TICalculation | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [calcError, setCalcError] = useState<string | null>(null)

  const fetchCalculations = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const statusParam = statusFilter || undefined
      const result = await ti.getAll({
        configId: configFilter || undefined,
        status: statusParam,
        period: periodFilter || undefined,
        limit,
        offset: (page - 1) * limit,
      })
      setCalculations(result.calculations)
      setTotal(result.total)
      setHasMore(result.hasMore)
    } catch (e) {
      setError((e as Error).message || 'Errore nel caricamento calcoli')
    }

    setIsLoading(false)
  }, [configFilter, statusFilter, periodFilter, page])

  const fetchConfigurations = useCallback(async () => {
    if (!user?.cer_id) return
    try {
      const result = await cer.getConfigurations(user.cer_id)
      setConfigurations(result.configurations.map(c => ({
        id: c.id,
        name: c.name,
        crin: c.crin,
      })))
      // Auto-select first config for new calculation
      if (result.configurations.length > 0 && !newCalcConfig) {
        setNewCalcConfig(result.configurations[0].id)
      }
    } catch (e) {
      console.error('Failed to fetch configurations:', e)
    }
  }, [user?.cer_id, newCalcConfig])

  useEffect(() => {
    fetchCalculations()
  }, [fetchCalculations])

  useEffect(() => {
    fetchConfigurations()
  }, [fetchConfigurations])

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [periodFilter, statusFilter, configFilter])

  const handlePreview = async () => {
    if (!newCalcConfig || !newCalcPeriod) return
    setIsCalculating(true)
    setCalcError(null)
    setPreview(null)

    try {
      const result = await ti.calculate({
        configId: newCalcConfig,
        period: newCalcPeriod,
        tiTotal: parseFloat(newCalcTiTotal) || 0,
        eaciTotal: parseFloat(newCalcEaciTotal) || 0,
        preview: true,
      })
      setPreview(result as unknown as TICalculation)
    } catch (e) {
      setCalcError((e as Error).message || 'Errore nel calcolo preview')
    }

    setIsCalculating(false)
  }

  const handleSave = async () => {
    if (!newCalcConfig || !newCalcPeriod) return
    setIsCalculating(true)
    setCalcError(null)

    try {
      await ti.calculate({
        configId: newCalcConfig,
        period: newCalcPeriod,
        tiTotal: parseFloat(newCalcTiTotal) || 0,
        eaciTotal: parseFloat(newCalcEaciTotal) || 0,
        preview: false,
      })
      setIsModalOpen(false)
      setPreview(null)
      setNewCalcPeriod('')
      setNewCalcTiTotal('')
      setNewCalcEaciTotal('')
      fetchCalculations()
    } catch (e) {
      setCalcError((e as Error).message || 'Errore nel salvataggio')
    }

    setIsCalculating(false)
  }

  const handleDelete = async (calc: TICalculation) => {
    if (!confirm(`Eliminare il calcolo ${formatPeriod(calc.period)}?`)) return

    try {
      await ti.delete(calc.id)
      fetchCalculations()
    } catch (e) {
      alert((e as Error).message || 'Errore eliminazione')
    }
  }

  const handleDownloadPDF = async (calc: TICalculation) => {
    try {
      const blob = await ti.downloadReport(calc.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ti-report-${calc.period}.html`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      alert((e as Error).message || 'Errore download')
    }
  }

  const getStatusBadge = (status: CalculationStatus) => {
    const variants: Record<CalculationStatus, { label: string; variant: 'default' | 'warning' | 'success' }> = {
      draft: { label: 'Bozza', variant: 'default' },
      approved: { label: 'Approvato', variant: 'warning' },
      distributed: { label: 'Distribuito', variant: 'success' },
    }
    return variants[status]
  }

  // Get current month in YYYY-MM format
  const getCurrentPeriod = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }

  return (
    <PageContainer>
      <PageHeader
        title="Calcoli TI"
        description={`${total} calcoli`}
        actions={
          <Button onClick={() => {
            setIsModalOpen(true)
            setPreview(null)
            setCalcError(null)
            if (!newCalcPeriod) setNewCalcPeriod(getCurrentPeriod())
          }}>
            + Nuovo Calcolo
          </Button>
        }
      />

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <input
                type="month"
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                placeholder="Periodo"
                className="h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as CalculationStatus | '')}
                className="h-10 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 text-sm"
              >
                <option value="">Tutti gli stati</option>
                <option value="draft">Bozza</option>
                <option value="approved">Approvato</option>
                <option value="distributed">Distribuito</option>
              </select>
              <select
                value={configFilter}
                onChange={(e) => setConfigFilter(e.target.value)}
                className="h-10 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 text-sm"
              >
                <option value="">Tutte le config</option>
                {configurations.map(c => (
                  <option key={c.id} value={c.id}>{c.crin}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculations Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-[var(--color-error)]">{error}</div>
          ) : calculations.length === 0 ? (
            <div className="py-12 text-center text-[var(--color-text-secondary)]">
              Nessun calcolo trovato
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Periodo
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Config
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      TI Totale
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.map((calc) => {
                    const statusBadge = getStatusBadge(calc.status as CalculationStatus)
                    const configName = configurations.find(c => c.id === calc.config_id)?.crin || calc.config_id
                    return (
                      <tr
                        key={calc.id}
                        className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)]/50"
                      >
                        <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                          {formatPeriod(calc.period)}
                        </td>
                        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
                          {configName}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {formatCurrency(calc.ti_total)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/admin/calculations/${calc.id}`}>
                              <Button variant="ghost" size="sm">
                                Dettagli
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPDF(calc)}
                            >
                              PDF
                            </Button>
                            {calc.status === 'draft' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(calc)}
                                className="text-[var(--color-error)] hover:text-[var(--color-error)]"
                              >
                                Elimina
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && calculations.length > 0 && (
            <div className="flex items-center justify-between border-t border-[var(--color-border)] px-4 py-3">
              <div className="text-sm text-[var(--color-text-secondary)]">
                Pagina {page} di {Math.ceil(total / limit) || 1}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Precedente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={!hasMore}
                >
                  Successivo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Calculation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-2xl rounded-xl bg-[var(--color-bg-primary)] p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Nuovo Calcolo TI</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">Configurazione</label>
                  <select
                    value={newCalcConfig}
                    onChange={(e) => setNewCalcConfig(e.target.value)}
                    className="h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3"
                  >
                    {configurations.map(c => (
                      <option key={c.id} value={c.id}>{c.crin}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Periodo</label>
                  <input
                    type="month"
                    value={newCalcPeriod}
                    onChange={(e) => setNewCalcPeriod(e.target.value)}
                    className="h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">TI Totale (EUR)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newCalcTiTotal}
                    onChange={(e) => setNewCalcTiTotal(e.target.value)}
                    placeholder="0.00"
                    className="h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">EACI Totale (kWh)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newCalcEaciTotal}
                    onChange={(e) => setNewCalcEaciTotal(e.target.value)}
                    placeholder="0.00"
                    className="h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3"
                  />
                </div>
              </div>

              {calcError && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                  {calcError}
                </div>
              )}

              {/* Preview Results */}
              {preview && (
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-4">
                  <h3 className="mb-3 font-medium">Anteprima Calcolo</h3>
                  <div className="grid gap-2 text-sm sm:grid-cols-3">
                    <div>
                      <span className="text-[var(--color-text-secondary)]">TI Totale:</span>{' '}
                      <span className="font-medium">{formatCurrency(preview.ti_total)}</span>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">EACI:</span>{' '}
                      <span className="font-medium">{preview.eaci_total.toLocaleString('it-IT')} kWh</span>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Quota Condivisione:</span>{' '}
                      <span className="font-medium">{preview.quota_condivisione.toFixed(2)}%</span>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Eccedenza:</span>{' '}
                      <span className="font-medium">{formatCurrency(preview.eccedenza)}</span>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Costi Gestione:</span>{' '}
                      <span className="font-medium">{formatCurrency(preview.costi_gestione)}</span>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Residuo:</span>{' '}
                      <span className="font-medium">{formatCurrency(preview.residuo)}</span>
                    </div>
                  </div>
                  {Array.isArray(preview.distribution) && preview.distribution.length > 0 && (
                    <div className="mt-3">
                      <span className="text-sm text-[var(--color-text-secondary)]">
                        {preview.distribution.length} membri nella distribuzione
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                  Annulla
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePreview}
                  disabled={isCalculating || !newCalcConfig || !newCalcPeriod}
                >
                  {isCalculating ? <Spinner size="sm" /> : 'Anteprima'}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isCalculating || !preview}
                >
                  {isCalculating ? <Spinner size="sm" /> : 'Salva'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  )
}
